import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { format } from 'date-fns';

/**
 * Hook for managing attendance data (Admin view)
 * @param {string} businessId - The current business ID
 * @param {Date|string} initialDate - Initial date for attendance (defaults to today)
 */
export const useAttendance = (businessId, initialDate = null) => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    initialDate ? new Date(initialDate) : new Date()
  );

  const fetchAttendance = useCallback(async (date = selectedDate) => {
    if (!businessId) {
      setAttendance([]);
      setEmployees([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const dateStr = date instanceof Date ? format(date, 'yyyy-MM-dd') : date;
      const [attRes, empRes] = await Promise.all([
        api.get(`/attendance?business_id=${businessId}&date=${dateStr}`),
        api.get(`/employees?business_id=${businessId}`)
      ]);
      setAttendance(attRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch attendance');
      console.error('Error fetching attendance:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, selectedDate]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // When selectedDate changes, refetch
  useEffect(() => {
    if (businessId) {
      fetchAttendance(selectedDate);
    }
  }, [selectedDate, businessId]);

  const checkIn = async (employeeId, time = null) => {
    const checkInTime = time || format(new Date(), 'HH:mm');
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const res = await api.post('/attendance', {
      employee_id: employeeId,
      business_id: businessId,
      date: dateStr,
      check_in: checkInTime,
      status: 'present'
    });
    await fetchAttendance();
    return res.data;
  };

  const checkOut = async (attendanceId, time = null) => {
    const checkOutTime = time || format(new Date(), 'HH:mm');
    const res = await api.put(`/attendance/${attendanceId}/checkout?check_out=${checkOutTime}`);
    await fetchAttendance();
    return res.data;
  };

  const updateAttendance = async (attendanceId, data) => {
    const params = new URLSearchParams();
    if (data.check_in !== undefined) params.append('check_in', data.check_in || '');
    if (data.check_out !== undefined) params.append('check_out', data.check_out || '');
    if (data.status !== undefined) params.append('status', data.status);
    if (data.notes !== undefined) params.append('notes', data.notes || '');
    
    const res = await api.put(`/attendance/${attendanceId}?${params.toString()}`);
    await fetchAttendance();
    return res.data;
  };

  const deleteAttendance = async (attendanceId) => {
    const res = await api.delete(`/attendance/${attendanceId}`);
    await fetchAttendance();
    return res.data;
  };

  const importBiometric = async (formData) => {
    const res = await api.post(`/attendance/import/biometric?business_id=${businessId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    await fetchAttendance();
    return res.data;
  };

  const downloadTemplate = async () => {
    const res = await api.get('/attendance/template/csv', { responseType: 'blob' });
    return res.data;
  };

  const exportAttendance = async (startDate, endDate, employeeId = null, exportFormat = 'csv') => {
    const startStr = startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate;
    const endStr = endDate instanceof Date ? format(endDate, 'yyyy-MM-dd') : endDate;
    
    let url = `/attendance/export/${exportFormat}?business_id=${businessId}&start_date=${startStr}&end_date=${endStr}`;
    if (employeeId && employeeId !== 'all') {
      url += `&employee_id=${employeeId}`;
    }
    const res = await api.get(url, { responseType: 'blob' });
    return res.data;
  };

  const exportAttendanceSalary = async (month, year) => {
    const url = `/exports/attendance-salary?business_id=${businessId}&month=${month}&year=${year}`;
    const res = await api.get(url, { responseType: 'blob' });
    return res.data;
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  // Stats
  const stats = {
    present: attendance.filter(a => a.status === 'present').length,
    absent: Math.max(0, employees.length - attendance.filter(a => a.status === 'present').length),
    late: attendance.filter(a => a.status === 'late').length,
    total: employees.length,
  };

  return {
    attendance,
    employees,
    loading,
    error,
    stats,
    selectedDate,
    setSelectedDate,
    fetchAttendance,
    checkIn,
    checkOut,
    updateAttendance,
    deleteAttendance,
    importBiometric,
    downloadTemplate,
    exportAttendance,
    exportAttendanceSalary,
    getEmployeeName,
  };
};

/**
 * Hook for attendance statistics (standalone)
 */
export const useAttendanceStats = (businessId, date = null) => {
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    late: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async (targetDate = date || new Date()) => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const dateStr = targetDate instanceof Date ? format(targetDate, 'yyyy-MM-dd') : targetDate;
      // Calculate stats from attendance data
      const attRes = await api.get(`/attendance?business_id=${businessId}&date=${dateStr}`);
      const empRes = await api.get(`/employees?business_id=${businessId}`);
      
      const presentCount = attRes.data.filter(a => a.status === 'present').length;
      const totalEmployees = empRes.data.length;
      
      setStats({
        present: presentCount,
        absent: totalEmployees - presentCount,
        late: attRes.data.filter(a => a.status === 'late').length,
        total: totalEmployees
      });
    } catch (err) {
      console.error('Error fetching attendance stats:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, date]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, fetchStats };
};
